import React, { useRef, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { SearchResult } from '../../constants/searchIndex';
import './SearchModal.css';

interface SearchModalProps {
  isOpen: boolean;
  query: string;
  results: SearchResult[];
  onQueryChange: (query: string) => void;
  onClose: () => void;
  onSelectResult: (result: SearchResult) => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({
  isOpen,
  query,
  results,
  onQueryChange,
  onClose,
  onSelectResult
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Focus input when modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelectResult = (result: SearchResult) => {
    onSelectResult(result);
    onClose();
  };

  return (
    <div className="search-modal-overlay" onClick={onClose}>
      <div className="search-modal-content" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="search-modal-header">
          <h2 className="search-modal-title">Search Prayers</h2>
          <button
            onClick={onClose}
            className="search-modal-close"
            aria-label="Close search"
          >
            <X size={24} />
          </button>
        </div>

        {/* Search Input */}
        <div className="search-modal-input-wrapper">
          <Search size={20} className="search-modal-input-icon" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search by name, saint, or cause..."
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            className="search-modal-input"
            onKeyDown={e => {
              if (e.key === 'Escape') {
                onClose();
              }
            }}
          />
          {query && (
            <button
              onClick={() => onQueryChange('')}
              className="search-modal-clear"
              aria-label="Clear search"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Results */}
        <div className="search-modal-results">
          {query === '' ? (
            <div className="search-modal-empty">
              <p>Start typing to search novenas and chaplets</p>
            </div>
          ) : results.length === 0 ? (
            <div className="search-modal-empty">
              <p>No prayers found matching "{query}"</p>
              <p className="search-modal-empty-hint">Try searching by saint name, intention, or prayer type</p>
            </div>
          ) : (
            <ul className="search-modal-list">
              {results.map(result => (
                <li
                  key={`${result.type}-${result.id}`}
                  className="search-modal-list-item"
                  onClick={() => handleSelectResult(result)}
                >
                  <div className="search-result-icon">{result.icon}</div>
                  <div className="search-result-content">
                    <h3 className="search-result-name">{result.name}</h3>
                    <p className="search-result-description">{result.description}</p>
                    {result.patron && (
                      <p className="search-result-meta">
                        <span className="search-result-label">Patron:</span> {result.patron}
                      </p>
                    )}
                    {result.feastDay && (
                      <p className="search-result-meta">
                        <span className="search-result-label">Feast:</span> {result.feastDay}
                      </p>
                    )}
                  </div>
                  <div className="search-result-type-badge">{result.type}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Results count */}
        {query && results.length > 0 && (
          <div className="search-modal-footer">
            <p>{results.length} result{results.length !== 1 ? 's' : ''} found</p>
          </div>
        )}
      </div>
    </div>
  );
};
